/**
 * ¿Volvieron a comprar? Cuenta, con la base de pedidos POSTERIOR al envío, quiénes de los que
 * recibieron el mensaje volvieron y cuánta plata trajeron.
 *
 * - WhatsApp oficial NO tiene grupo de control (decisión de Alan): no hay contra qué comparar, así
 *   que esto no es el efecto del mensaje. La tarjeta lo dice siempre (`TEXTO_MEDICION_SIN_CONTROL`),
 *   también para una campaña medida antes de sacar el control.
 * - Lo simulado no se mide: no salió ningún mensaje de verdad.
 * - Sin base posterior, se pide cargarla (los mismos dos CSV que el análisis de Activa).
 *
 * Restricción (D-D): la base se carga en el mes siguiente al de la audiencia; si la marca ya tiene
 * otra campaña de Activa analizada en ese mes, cargarla pisaría su base, y el backend responde 422
 * `PERIODO_OCUPADO`. Ese texto se muestra tal cual: explica qué hacer.
 */

import { useId, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, TrendingUp, UploadCloud } from "lucide-react";
import type { CampanaDetalle, Medicion, Resultado } from "../types";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { oficialApi, seguimientoExcedeTope } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ars } from "../logic/formato";
import { TEXTO_FALTAN_COMPRAS_POSTERIORES, TEXTO_MEDICION_SIN_CONTROL, advertenciasMedicion } from "../logic/textos";
import { fechaHora, num, plural } from "../../campana-nueva/utils/format";
import { textoDeCsv } from "../logic/archivo";

const tasaTexto = (t: number) => `${(Math.round((Number(t) || 0) * 1000) / 10).toLocaleString("es-AR")} %`;

/** Texto para un rechazo de la carga del seguimiento. */
function textoRechazoSeguimiento(code: string, error: string): string {
  if (code === "MISSING_MINIMUMS" || code === "REQUIERE_CONFIRMACION" || code === "MAPEO_INVALIDO") {
    return `No reconocimos las columnas del archivo: ${error || "faltan columnas"}. Usá el mismo formato que en el análisis.`;
  }
  if (code === "SIN_ENVIOS") return "Esta campaña no mandó nada todavía.";
  // PERIODO_OCUPADO y cualquier otro: el texto del backend dice qué pasó y qué hacer.
  return error || "No se pudo cargar el seguimiento.";
}

interface Props {
  campana: CampanaDetalle;
  resultado: Resultado | null;
  onMedido: (m: Medicion) => void;
}

export function MedicionRecompra({ campana, resultado, onMedido }: Props) {
  // Cargar el seguimiento y volver a medir es del equipo de Deenex: un cliente ve la medición.
  const equipo = useEsEquipoDeenex();
  const idPedidos = useId();
  const idClientes = useId();
  const [archivos, setArchivos] = useState<{ pedidos?: string; clientes?: string }>({});
  const [busy, setBusy] = useState<"" | "seguimiento" | "medir">("");
  const [error, setError] = useState("");
  const medicion = resultado?.medicion ?? null;

  const leerArchivo = (cual: "pedidos" | "clientes") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    textoDeCsv(f).then(
      (texto) => setArchivos((prev) => ({ ...prev, [cual]: texto })),
      () => setError("No pudimos leer el archivo."),
    );
  };

  const cargarSeguimiento = async () => {
    if (!archivos.pedidos || !archivos.clientes) return;
    const s = { pedidos: { content: archivos.pedidos, encoding: "csv" as const }, clientes: { content: archivos.clientes, encoding: "csv" as const } };
    // Mismo tope que el controller: se avisa antes de subir 9 MB para recibir un 413.
    if (seguimientoExcedeTope(s)) { setError("Los archivos pesan más de 8 MB entre los dos."); return; }
    setBusy("seguimiento");
    setError("");
    try {
      const l = leerRespuesta<Medicion>(await oficialApi.seguimiento(campana.id, s));
      if (!l.ok) { setError(textoRechazoSeguimiento(l.code, l.error)); return; }
      setArchivos({});
      onMedido(l.data);
    } catch (e) {
      const x = leerError(e);
      setError(x.status === 413 ? "Los archivos pesan más de 8 MB." : textoError(x));
    } finally {
      setBusy("");
    }
  };

  const medir = async () => {
    setBusy("medir");
    setError("");
    try {
      const l = leerRespuesta<Medicion>(await oficialApi.medir(campana.id));
      if (!l.ok) { setError(l.error || textoError(l)); return; }
      onMedido(l.data);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setBusy("");
    }
  };

  if (campana.simulado || (medicion && !medicion.disponible && medicion.motivo === "SIMULADO")) {
    return (
      <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Los envíos simulados no se miden: no salió ningún mensaje de verdad.
      </p>
    );
  }

  if (medicion?.disponible) {
    const recibieron = medicion.entregados;
    return (
      <div className="space-y-4">
        <p className="text-lg text-slate-900" data-testid="medicion-principal">
          <strong className="text-emerald-700">{num(recibieron.volvieron)}</strong> de {num(recibieron.n)}{" "}
          {plural(recibieron.n, "persona que recibió", "personas que recibieron")} el mensaje{" "}
          {plural(recibieron.volvieron, "volvió", "volvieron")} a comprar ({tasaTexto(recibieron.tasa)})
        </p>
        <p className="text-sm text-slate-600" data-testid="medicion-plata">{ars(recibieron.plata)} en compras nuevas de los que recibieron el mensaje</p>
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="medicion-sin-control">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {TEXTO_MEDICION_SIN_CONTROL}
        </p>
        <p className="text-xs text-slate-500">
          Contando también a los que no les llegó (todos los asignados al envío): volvieron {num(medicion.envio.volvieron)} de {num(medicion.envio.n)} ({tasaTexto(medicion.envio.tasa)}).
        </p>
        {medicion.atribucion === "pendiente-base-nueva" && (
          // «Base posterior» es jerga del motor: lo que falta son las compras de después del envío
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{TEXTO_FALTAN_COMPRAS_POSTERIORES}</p>
        )}
        {advertenciasMedicion(medicion.advertencias).map((a, i) => (
          <p key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {a}
          </p>
        ))}
        <div className="flex items-center gap-3 flex-wrap">
          {equipo && (
            <button type="button" onClick={() => void medir()} disabled={!!busy}
              className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === "medir" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Volver a medir
            </button>
          )}
          <span className="text-[11px] text-slate-400">Medido el {fechaHora(medicion.medidoAt)}</span>
        </div>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      </div>
    );
  }

  if (!equipo) {
    return (
      <p className="flex items-start gap-2 text-sm text-slate-700">
        <TrendingUp size={15} className="mt-0.5 text-emerald-500 flex-shrink-0" />
        Todavía no se midió quién volvió a comprar. La medición la carga el equipo de Deenex: cuando esté, la vas a ver acá.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="flex items-start gap-2 text-sm text-slate-700">
        <TrendingUp size={15} className="mt-0.5 text-emerald-500 flex-shrink-0" />
        Para saber quién volvió a comprar, subí los archivos de pedidos y clientes de después del envío.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={idPedidos} className="text-xs font-semibold text-slate-600">Pedidos (CSV)</label>
          <input id={idPedidos} type="file" accept=".csv,text/csv" onChange={leerArchivo("pedidos")} className="mt-1 block w-full text-xs" />
          {archivos.pedidos !== undefined && <p className="text-[11px] text-emerald-700 mt-1">Listo</p>}
        </div>
        <div>
          <label htmlFor={idClientes} className="text-xs font-semibold text-slate-600">Clientes (CSV)</label>
          <input id={idClientes} type="file" accept=".csv,text/csv" onChange={leerArchivo("clientes")} className="mt-1 block w-full text-xs" />
          {archivos.clientes !== undefined && <p className="text-[11px] text-emerald-700 mt-1">Listo</p>}
        </div>
      </div>
      <button type="button" onClick={() => void cargarSeguimiento()} disabled={!!busy || !archivos.pedidos || !archivos.clientes}
        className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2">
        {busy === "seguimiento" ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Medir
      </button>
      {medicion && !medicion.disponible && medicion.mensaje && <p className="text-xs text-slate-500">{medicion.mensaje}</p>}
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>}
    </div>
  );
}

export default MedicionRecompra;
